import PasswordModal from '../components/modal/PasswordModal';
import { createContext, useCallback, useContext, useState } from 'react';

type ResolveFn = (password: string | null) => void;

interface ModalContextValue {
  requestPassword: (reason?: string) => Promise<string | null>;
}

const PasswordModalContext = createContext<ModalContextValue | undefined>(undefined);

export const usePasswordModal = () => {
  const context = useContext(PasswordModalContext);
  if (!context) throw new Error('usePasswordModal must be used within PasswordModalProvider');
  return context;
};

export const PasswordModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState<string | undefined>();
  const [resolveFn, setResolveFn] = useState<ResolveFn | null>(null);

  const requestPassword = useCallback((reason?: string): Promise<string | null> => {
    setIsOpen(true);
    setReason(reason);
    return new Promise(resolve => {
      setResolveFn(() => resolve);
    });
  }, []);

  const handleConfirm = (password: string) => {
    if (resolveFn) resolveFn(password);
    closeModal();
  };

  const handleCancel = () => {
    if (resolveFn) resolveFn(null);
    closeModal();
  };

  const closeModal = () => {
    setIsOpen(false);
    setResolveFn(null);
    setReason(undefined);
  };

  return (
    <PasswordModalContext.Provider value={{ requestPassword }}>
      {children}
      {isOpen && (
        <PasswordModal reason={reason} onConfirm={handleConfirm} onCancel={handleCancel} />
      )}
    </PasswordModalContext.Provider>
  );
};
